import { FC } from 'react';

import _ from 'lodash';
import { useFormContext } from 'react-hook-form';

import { FormLabel } from 'components/FormLabel/FormLabel';
import { If } from 'components/Generics';
import RhfRadioGroup from 'components/RadioGroup/RhfRadioGroup/RhfRadioGroup';
import { RHFTextArea } from 'components/RHFTextArea/RHFTextArea';
import { CANINE, MOLAR } from 'constants/prescriptionConstants';
import {
  SAGITTAL_ACTIONS_VALUES,
  SAGITTAL_RELATIONSHIP_OPTIONS,
  SAGITTAL_RELATIONSHIP_VALUES
} from 'constants/prescriptionOptions';
import { Option } from 'types/select';
import { translate } from 'utils';

import SagittalImprove from './SagittaImprove';
import SagittalAction from './SagittalAction';

const DEFAULT_FIELD_PREFIX =
  'treatmentSpecifications.ZenClearUltra.sagittalRelationship';

interface Props {
  isDisabled?: boolean;
  fieldPrefix?: string;
}

const SagittalRelationship: FC<Props> = ({
  isDisabled,
  fieldPrefix = DEFAULT_FIELD_PREFIX
}) => {
  const {
    watch,
    formState: { errors }
  } = useFormContext();
  const watchSagittalRelationShip = watch(`${fieldPrefix}.relationship`);
  const watchRightAndLeftActions = watch(
    `${fieldPrefix}.bothRandL.rightAndLeftAction`
  );
  const watchRightAction = watch(`${fieldPrefix}.eitherRorL.right.rightAction`);
  const watchLeftAction = watch(`${fieldPrefix}.eitherRorL.left.leftAction`);
  const fieldError = _.get(errors, fieldPrefix);
  const isOptionChecked = (value: string, selectedItems: Option[]) => {
    return selectedItems?.find((item: any) => item?.value === value);
  };

  const isSagittalBothImproveAny =
    isOptionChecked(
      SAGITTAL_ACTIONS_VALUES.IMPROVE_CANINE_RELATIONSHIP,
      watchRightAndLeftActions
    ) ||
    isOptionChecked(
      SAGITTAL_ACTIONS_VALUES.IMPROVE_MOLAR_RELATIONSHIP,
      watchRightAndLeftActions
    );
  const isSagittalRightImprove =
    isOptionChecked(
      SAGITTAL_ACTIONS_VALUES.IMPROVE_CANINE_RELATIONSHIP,
      watchRightAction
    ) ||
    isOptionChecked(
      SAGITTAL_ACTIONS_VALUES.IMPROVE_MOLAR_RELATIONSHIP,
      watchRightAction
    );
  const isSagittalLeftImprove =
    isOptionChecked(
      SAGITTAL_ACTIONS_VALUES.IMPROVE_CANINE_RELATIONSHIP,
      watchLeftAction
    ) ||
    isOptionChecked(
      SAGITTAL_ACTIONS_VALUES.IMPROVE_MOLAR_RELATIONSHIP,
      watchLeftAction
    );
  const getAfterAlignmentTextBoxes = (fieldName: string) => {
    return (
      <div className='p-4 bg-CHINESE_WHITE rounded-2xl my-4'>
        <RHFTextArea
          name={`${fieldName}.text`}
          autogrow
          inputProps={{
            disabled: isDisabled,
            placeholder: translate('settings.please_type_here')
          }}
          maxgrowHeight={260}
        />
      </div>
    );
  };

  return (
    <div>
      <FormLabel
        label={translate('prescription.step10_sagittal_relationship')}
        className='md:text-17px mb-4'
        error={!!fieldError}
        name={'sagittalRelationship'}
      />
      <RhfRadioGroup
        inputProps={{ disabled: isDisabled }}
        options={SAGITTAL_RELATIONSHIP_OPTIONS}
        name={`${fieldPrefix}.relationship`}
        className='mt-2 flex flex-row space-x-16'
        radioClassName='border-RADIO_BORDER'
        radioItemFont='md:text-17px text-sm '
      />
      <If
        condition={
          watchSagittalRelationShip ===
          SAGITTAL_RELATIONSHIP_VALUES.RIGHT_AND_LEFT
        }
      >
        <div className='p-4 rounded-2xl bg-GRAY5 mt-4'>
          <SagittalAction
            isDisabled={isDisabled}
            fieldName={`${fieldPrefix}.bothRandL.rightAndLeftAction`}
          />
          {isSagittalBothImproveAny && (
            <div className='p-4 rounded-2xl bg-CHINESE_WHITE'>
              {isOptionChecked(
                SAGITTAL_ACTIONS_VALUES.IMPROVE_CANINE_RELATIONSHIP,
                watchRightAndLeftActions
              ) && (
                <SagittalImprove
                  isDisabled={isDisabled}
                  fieldName={`${fieldPrefix}.bothRandL.improveCanine`}
                  sideTitle='sagittal_relationship.improve_canine_relationship'
                  improvementType={CANINE}
                />
              )}
              {isOptionChecked(
                SAGITTAL_ACTIONS_VALUES.IMPROVE_MOLAR_RELATIONSHIP,
                watchRightAndLeftActions
              ) && (
                <SagittalImprove
                  isDisabled={isDisabled}
                  sideTitle='sagittal_relationship.improve_molar_relationship'
                  fieldName={`${fieldPrefix}.bothRandL.improveMolar`}
                  improvementType={MOLAR}
                />
              )}
            </div>
          )}
          {isOptionChecked(
            SAGITTAL_ACTIONS_VALUES.SHOW_RESULTING_AFTER_ALIGNMENT,
            watchRightAndLeftActions
          ) &&
            getAfterAlignmentTextBoxes(
              `${fieldPrefix}.bothRandL.afterAlignment`
            )}
        </div>
      </If>
      <If
        condition={
          watchSagittalRelationShip ===
          SAGITTAL_RELATIONSHIP_VALUES.RIGHT_OR_LEFT
        }
      >
        <div className='p-4 rounded-2xl bg-GRAY5 mt-4'>
          <div className='flex flex-col'>
            <div className=' flex flex-row space-x-4 mb-2 '>
              <div className='mt-3 bg-black text-white flex justify-center items-center  w-6 h-6 rounded-full'>
                <span>R</span>
              </div>

              <SagittalAction
                isDisabled={isDisabled}
                fieldName={`${fieldPrefix}.eitherRorL.right.rightAction`}
              />
            </div>
            {isSagittalRightImprove && (
              <div className='p-4 rounded-2xl bg-CHINESE_WHITE'>
                {isOptionChecked(
                  SAGITTAL_ACTIONS_VALUES.IMPROVE_CANINE_RELATIONSHIP,
                  watchRightAction
                ) && (
                  <SagittalImprove
                    isDisabled={isDisabled}
                    fieldName={`${fieldPrefix}.eitherRorL.right.improveCanine`}
                    sideTitle='sagittal_relationship.improve_canine_relationship'
                    improvementType={CANINE}
                  />
                )}
                {isOptionChecked(
                  SAGITTAL_ACTIONS_VALUES.IMPROVE_MOLAR_RELATIONSHIP,
                  watchRightAction
                ) && (
                  <SagittalImprove
                    isDisabled={isDisabled}
                    sideTitle='sagittal_relationship.improve_molar_relationship'
                    fieldName={`${fieldPrefix}.eitherRorL.right.improveMolar`}
                    improvementType={MOLAR}
                  />
                )}
              </div>
            )}
            {isOptionChecked(
              SAGITTAL_ACTIONS_VALUES.SHOW_RESULTING_AFTER_ALIGNMENT,
              watchRightAction
            ) &&
              getAfterAlignmentTextBoxes(
                `${fieldPrefix}.eitherRorL.right.afterAlignment`
              )}
          </div>
          <div className='flex flex-col'>
            <div className=' flex flex-row space-x-4 mb-2 '>
              <div className='mt-3 bg-black text-white flex justify-center items-center  w-6 h-6 rounded-full'>
                <span>L</span>
              </div>

              <SagittalAction
                isDisabled={isDisabled}
                fieldName={`${fieldPrefix}.eitherRorL.left.leftAction`}
              />
            </div>
            {isSagittalLeftImprove && (
              <div className='p-4 rounded-2xl bg-CHINESE_WHITE'>
                {isOptionChecked(
                  SAGITTAL_ACTIONS_VALUES.IMPROVE_CANINE_RELATIONSHIP,
                  watchLeftAction
                ) && (
                  <SagittalImprove
                    isDisabled={isDisabled}
                    fieldName={`${fieldPrefix}.eitherRorL.left.improveCanine`}
                    sideTitle='sagittal_relationship.improve_canine_relationship'
                    improvementType={CANINE}
                  />
                )}
                {isOptionChecked(
                  SAGITTAL_ACTIONS_VALUES.IMPROVE_MOLAR_RELATIONSHIP,
                  watchLeftAction
                ) && (
                  <SagittalImprove
                    isDisabled={isDisabled}
                    sideTitle='sagittal_relationship.improve_molar_relationship'
                    fieldName={`${fieldPrefix}.eitherRorL.left.improveMolar`}
                    improvementType={MOLAR}
                  />
                )}
              </div>
            )}
            {isOptionChecked(
              SAGITTAL_ACTIONS_VALUES.SHOW_RESULTING_AFTER_ALIGNMENT,
              watchLeftAction
            ) &&
              getAfterAlignmentTextBoxes(
                `${fieldPrefix}.eitherRorL.left.afterAlignment`
              )}
          </div>
        </div>
      </If>
    </div>
  );
};
export default SagittalRelationship;
